export const isErrorBody = (value: unknown): value is ErrorBody<unknown> => {
    if (!value) return false;
    const converted = value as ErrorBody<unknown>;
    if (!converted || !converted.error || !converted.message) return false;
    return true;
};

export interface ErrorBody<T> {
    statusCode: number;
    error: string;
    message: string;
    details?: T;
}

export interface ApiErrorBody<T = unknown> extends ErrorBody<T> {
    errorId?: string;
}

const INSCRIPTION_SUBMIT_ERROR_MESSAGES: Record<string, string> = {
    "errors.member.already_preregistered": "Tu es déjà pré-inscrit pour cette saison, patiente pour ta validation",
};

export const getInscriptionSubmitErrorMessage = (error: unknown): string => {
    if (!error || typeof error !== "object" || !("response" in error)) {
        return "Une erreur est survenue lors de l'envoi du formulaire. Réessaie ou contacte le club.";
    }

    const response: unknown = (error as { response?: { data?: unknown } }).response?.data;
    if (!isErrorBody(response)) {
        return "Une erreur est survenue lors de l'envoi du formulaire. Réessaie ou contacte le club.";
    }

    const apiError: ApiErrorBody = response;
    if (apiError.errorId !== undefined && INSCRIPTION_SUBMIT_ERROR_MESSAGES[apiError.errorId] !== undefined) {
        return INSCRIPTION_SUBMIT_ERROR_MESSAGES[apiError.errorId];
    }

    return apiError.message;
};

export interface ObjectWithStatusCode {
    statusCode: number;
}

export const isObjectWithStatusCode = (value: unknown): value is ObjectWithStatusCode => {
    return !!value && !!(value as ObjectWithStatusCode).statusCode;
};

export type ResultWithStatusCode<T> = { result: T; statusCode: number };

export type TranslatedError = { message: string; errorId: string };

export const unknownError = (e: unknown): ErrorBody<unknown> => {
    let statusCode = 500;
    if (isObjectWithStatusCode(e)) {
        statusCode = e.statusCode;
    }
    const errorId = "errors.unknown";
    return {
        statusCode,
        error: "Server Error",
        message: errorId,
        details: e,
    };
};

export const es5Err = (e: Error): ErrorBody<Error> => {
    const errorId = "errors.unknown";
    return {
        statusCode: 500,
        error: e.name,
        message: errorId,
        details: e,
    };
};

export const badRequest = <T>(tradKey: string, details?: T): ErrorBody<T> => {
    return {
        statusCode: 400,
        error: "Bad Request",
        message: tradKey,
        details: details,
    };
};

export const unauthorized = <T>(tradKey?: string, details?: T): ErrorBody<T> => {
    return {
        statusCode: 401,
        error: "Unauthorized",
        message: tradKey ?? "errors.unauthorized",
        details: details,
    };
};

export const invalidCredentials = <T>(): ErrorBody<T> => {
    const errorId = "errors.invalid_credentials";
    return {
        statusCode: 400,
        error: "Bad Request",
        message: errorId,
    };
};

export const emailUnverified = <T>(): ErrorBody<T> => {
    const errorId = "errors.user.unverified_email";
    return {
        statusCode: 401,
        error: "Unauthorized",
        message: errorId,
    };
};
export const accountBlocked = <T>(): ErrorBody<T> => {
    const errorId: string = "errors.blocked_account";
    return {
        statusCode: 401,
        error: "Unauthorized",
        message: errorId,
    };
};

export const shouldResetPassword = <T>(): ErrorBody<T> => {
    const errorId = "errors.user.should_reset_password";
    return {
        statusCode: 401,
        error: "Unauthorized",
        message: errorId,
    };
};
export const notFoundError = <T>(tradKey: string = "errors.not_found"): ErrorBody<T> => {
    return {
        statusCode: 404,
        error: "Not Found",
        message: tradKey,
    };
};
export const notImplemented = <T>(details?: T): ErrorBody<T> => {
    const errorId = "errors.server.not_implemented";
    return {
        statusCode: 501,
        error: "Not implemented",
        message: errorId,
        details: details,
    };
};
export const deprecated = <T>(): ErrorBody<T> => {
    const errorId = "errors.server.deprecated";
    return {
        statusCode: 500,
        error: "Not implemented",
        message: errorId,
    };
};
export const internalServerError = <T>(tradKey: string, details?: T): ErrorBody<T> => {
    return {
        statusCode: 500,
        error: "Internal Server Error",
        message: tradKey,
        details: details,
    };
};

export const internalPrismaError = <T>(tradKey: string, details?: T): ErrorBody<T> => {
    return {
        statusCode: 500,
        error: "Internal Server Error (prisma)",
        message: tradKey,
        details: details,
    };
};

export const serverUnavailable = <T>(tradKey: string, details?: T): ErrorBody<T> => {
    return {
        statusCode: 503,
        error: "Service Temporarily Unavailable",
        message: tradKey,
        details: details,
    };
};
